import i18n from '@/utils/i18n';
import { useCallback } from 'react';
import { useSalesPortalCreateActivityNoteMutation } from '@/experiences/sales-portal/blocs/documents/sales-portal-create-activity-note.generated';

interface CreateActivityNoteParams {
  chatUUID: string;
  noteContent: string;
}

interface CreatedActivity {
  chatUUID: string;
  uuid: string;
}

interface CreateActivityNoteMutatorOptions {
  onError?: (error: Error) => void;
  onSuccess?: (activity: CreatedActivity) => void;
}

interface CreateActivityNoteMutatorResult {
  createNote: (
    params: CreateActivityNoteParams,
  ) => Promise<CreatedActivity | null>;
  loading: boolean;
}

/**
 * Hook for creating activity notes.
 *
 * @param options.onError - Optional error handler
 * @param options.onSuccess - Optional success handler
 *
 * @returns {@link CreateActivityNoteMutatorResult}
 *
 * @example
 * ```typescript
 * const { createNote, loading } = useCreateActivityNoteMutator({
 *   onSuccess: (activity) => console.log('Created activity:', activity.uuid),
 *   onError: (err) => showErrorToast(err.message),
 * });
 * await createNote({ chatUUID: '123', noteContent: 'Meeting notes...' });
 * ```
 */
export const useCreateActivityNoteMutator = (
  options?: CreateActivityNoteMutatorOptions,
): CreateActivityNoteMutatorResult => {
  const [createActivityNoteMutation, { loading }] =
    useSalesPortalCreateActivityNoteMutation();

  const createNote = useCallback<
    (params: CreateActivityNoteParams) => Promise<CreatedActivity | null>
  >(
    async (params) => {
      const { chatUUID, noteContent } = params;

      try {
        const result = await createActivityNoteMutation({
          variables: {
            chatUUID,
            noteContent,
          },
        });

        const createdActivity = result.data?.createNoteActivity?.crm_activity;
        if (!createdActivity) {
          throw new Error(i18n.t('sales_portal.activity_note.error'));
        }

        const activity: CreatedActivity = {
          chatUUID: createdActivity.chat_uuid,
          uuid: createdActivity.uuid,
        };

        options?.onSuccess?.(activity);
        return activity;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        options?.onError?.(error);
        return null;
      }
    },
    [createActivityNoteMutation, options],
  );

  return {
    createNote,
    loading,
  };
};
